// React
import { ReactElement, useEffect, useState } from 'react';
// Next
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
// Context
import { useDashboard } from '@utmb/contexts/DashboardContext';
import { useCountry } from '@utmb/contexts/CountryContext';
// Modules
import { ListboxOption } from '@reach/listbox';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
// Interfaces
import { DashboardModalFormProps } from '@utmb/types/dashboard-modal';
import UserOrganizerUpdateStatusView from '@utmb/types/view/UserOrganizerUpdateStatusView';
import { UserOrganizerUpdateStatusEnum } from '@utmb/types/enum/UserOrganizerUpdateStatusEnum';
// Design System
import { Font } from '@utmb/design-system/text/font';
import { Input } from '@utmb/design-system/input/Input';
import { InputTel } from '@utmb/design-system/input-tel/InputTel';
import { Select } from '@utmb/design-system/select/Select';
import { InputMessage } from '@utmb/design-system/input-message/InputMessage';
import { Checkbox } from '@utmb/design-system/checkbox/Checkbox';
// Features
import { ActionsButtons } from '@utmb/features/user-edit-wrapper/ActionsButtons';
import { getKeycloakToken } from '@utmb/features/sso/keycloak';
// Utils
import { FetchMethodsEnum, fetchTheAPI } from '@utmb/utils/utmb';
// Types
import { DashboardOrganizerView } from '@utmb/types/index';
import { InputLanguage } from '@utmb/types/input-language';
import { MainLanguagesEnum } from '@utmb/types/main-languages-enum';
import CountryView from '@utmb/types/view/CountryView';
// Styles
import styles from './organizer-form.module.scss';

export const OrganizerForm = (props: DashboardModalFormProps): ReactElement | null => {
    const { onCancel } = props;

    const [phoneErrorMessage, setPhoneErrorMessage] = useState('');

    const { t } = useTranslation();
    const router = useRouter();
    const { dashboardOrganizer, updateDashboardOrganizerInformation } = useDashboard();
    const { countries, initCountries } = useCountry();

    useEffect(() => {
        initCountries();
    }, []);

    const { city, countryCode, phone, name, optinWorldSeries, optinUtmbLive, subdivisionId, preferredLanguage, fallbackLanguage } =
        dashboardOrganizer || ({} as DashboardOrganizerView);
    const noInputError = t('contactForm.noInputError');
    const telNotValidError = t('form.telNotValidError');

    const isMainLanguageEnum = dashboardOrganizer ? dashboardOrganizer?.preferredLanguage in MainLanguagesEnum : false;
    const [showFallbackLanguage, setShowFallbackLanguage] = useState<boolean>(!isMainLanguageEnum || false);

    const languages: InputLanguage[] =
        router.locales?.map((locale) => ({
            value: locale.toUpperCase(),
            label: t(`common.languages.${locale}`),
        })) || [];

    const fallbackLanguages: InputLanguage[] =
        Object.values(MainLanguagesEnum)
            .filter((value) => typeof value === 'string')
            .map((locale) => {
                const lowerCaseLocale = (locale as string).toLowerCase();

                return {
                    value: locale as string,
                    label: t(`common.languages.${lowerCaseLocale}`),
                };
            }) || [];

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            city,
            countryCode,
            phone,
            fullname: name || '',
            optinWorldSeries,
            optinUtmbLive,
            subdivisionId,
            preferredLanguage,
            fallbackLanguage,
        },
        validationSchema: Yup.object({
            fullname: Yup.string()
                .max(255, t('form.lengthExceededError', { length: 255 }))
                .required(noInputError),
            city: Yup.string()
                .max(255, t('form.lengthExceededError', { length: 255 }))
                .required(noInputError),
            countryCode: Yup.string()
                .length(2, t('form.countryNotSelectedError', { length: 2 }))
                .required(noInputError),
            phone: Yup.string()
                .max(255, t('form.lengthExceededError', { length: 255 }))
                .required(noInputError),
        }),
        onSubmit: async (values) => {
            const token = await getKeycloakToken();

            const { phone, city, countryCode, fullname, fallbackLanguage, optinWorldSeries, optinUtmbLive, preferredLanguage, subdivisionId } = values;

            const body = {
                name: fullname,
                phone,
                city,
                countryCode,
                subdivisionId,
                preferredLanguage,
                fallbackLanguage,
                optinWorldSeries,
                optinUtmbLive,
            };

            const response = (await fetchTheAPI({
                isClient: true,
                path: '/users/dashboard/organizer/information',
                bearer: token,
                method: FetchMethodsEnum.PUT,
                body,
            })) as UserOrganizerUpdateStatusView;

            if (response.statusCode === UserOrganizerUpdateStatusEnum.OK) {
                toast.success(t('myutmb:myUtmb.myInformation.successMessage'));
                updateDashboardOrganizerInformation(body as DashboardOrganizerView);
                onCancel();
            } else {
                toast.error(t('myutmb:myUtmb.myInformation.errorMessage'));
            }
        },
    });

    if (!dashboardOrganizer) {
        return null;
    }

    return (
        <form onSubmit={formik.handleSubmit} className={styles.form}>
            <div className={`${styles.input_container} ${styles.fullwidth}`}>
                <Input
                    id="fullname"
                    name="fullname"
                    type="text"
                    label={t('myutmb:myUtmb.registration.organizer-name')}
                    placeholder={t('myutmb:myUtmb.registration.organizer-name')}
                    value={formik.values.fullname}
                    onchange={formik.handleChange}
                    onblur={formik.handleBlur}
                    require={true}
                />
                {formik.touched.fullname && formik.errors.fullname && <InputMessage type="error" message={formik.errors.fullname} />}
            </div>
            <div className={styles.input_container}>
                <Input
                    id="city"
                    name="city"
                    type="text"
                    label={t('myutmb:myUtmb.registration.city')}
                    placeholder={t('myutmb:myUtmb.registration.city')}
                    value={formik.values.city}
                    onchange={formik.handleChange}
                    onblur={formik.handleBlur}
                    require={true}
                />
                {formik.touched.city && formik.errors.city && <InputMessage type="error" message={formik.errors.city} />}
            </div>
            <div className={styles.input_container}>
                <InputTel
                    id="phone"
                    name="phone"
                    label={t('myutmb:myUtmb.myInformation.contact.phone')}
                    value={formik.values.phone}
                    onChange={(fullNumber: string, value: string, isValid: boolean) => {
                        formik.setFieldValue('phone', fullNumber);
                        setPhoneErrorMessage(!isValid ? telNotValidError : '');
                    }}
                    onBlur={() => {
                        formik.setFieldTouched('phone', true);
                    }}
                    required={true}
                />
                {formik.touched.phone && (formik.errors.phone || !!phoneErrorMessage.length) && (
                    <InputMessage type="error" message={formik.errors.phone || phoneErrorMessage} />
                )}
            </div>
            <div className={styles.input_container}>
                <Select
                    id="countryCode"
                    name="countryCode"
                    label={t('myutmb:myUtmb.myInformation.contact.country')}
                    firstOptionlabel={t('common.selectCountry')}
                    onchange={(value: string) => {
                        formik.getFieldHelpers('countryCode').setValue(value);
                    }}
                    value={formik.values.countryCode}
                    require={true}
                >
                    {countries?.map((country: CountryView, key: number) => (
                        <ListboxOption key={`countrycode_${key}`} value={country.code} label={country.name}>
                            <Font.FuturaBook as="span" mobile="14" desktop="16">
                                {country.name}
                            </Font.FuturaBook>
                        </ListboxOption>
                    ))}
                </Select>
                {formik.touched.countryCode && formik.errors.countryCode && <InputMessage type="error" message={formik.errors.countryCode} />}
            </div>
            <div className={styles.input_container}>
                <Select
                    id="preferredLanguage"
                    name="preferredLanguage"
                    label="Communication language"
                    firstOptionlabel="Select a language"
                    onchange={(value: string) => {
                        formik.getFieldHelpers('preferredLanguage').setValue(value);

                        setShowFallbackLanguage(!(value.toUpperCase() in MainLanguagesEnum));
                    }}
                    value={formik.values.preferredLanguage}
                    require={true}
                >
                    {languages.map((language: InputLanguage, key: number) => (
                        <ListboxOption key={`preferredLanguage_${key}`} value={language.value} label={language.label}>
                            <Font.FuturaBook as="span" mobile="14" desktop="16">
                                {language.label}
                            </Font.FuturaBook>
                        </ListboxOption>
                    ))}
                </Select>
                {formik.touched.preferredLanguage && formik.errors.preferredLanguage && <InputMessage type="error" message={formik.errors.preferredLanguage} />}
            </div>

            {showFallbackLanguage && (
                <div className={styles.input_container}>
                    <Select
                        id="fallbackLanguage"
                        name="fallbackLanguage"
                        label="Other spoken language (optional)"
                        firstOptionlabel="Select a language"
                        onchange={(value: string) => {
                            formik.getFieldHelpers('fallbackLanguage').setValue(value);
                        }}
                        value={formik.values.fallbackLanguage}
                        require={true}
                    >
                        {fallbackLanguages.map((language: InputLanguage, key: number) => (
                            <ListboxOption key={`fallbackLanguage_${key}`} value={language.value} label={language.label}>
                                <Font.FuturaBook as="span" mobile="14" desktop="16">
                                    {language.label}
                                </Font.FuturaBook>
                            </ListboxOption>
                        ))}
                    </Select>
                    {formik.touched.fallbackLanguage && formik.errors.fallbackLanguage && (
                        <InputMessage type="error" message={formik.errors.fallbackLanguage} />
                    )}
                </div>
            )}

            <div className={`${styles.input_container} ${styles.fullwidth}`}>
                <Checkbox
                    id="optInWs"
                    name="optInWs"
                    checked={formik.values.optinWorldSeries}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label={t('myutmb:myUtmb.registration.optinWorldSeries')}
                />
                <Font.FuturaBook mobile="16" desktop="18">
                    <label htmlFor="optinWorldSeries">
                        <span dangerouslySetInnerHTML={{ __html: t('myutmb:myUtmb.registration.optinWorldSeries') }} />
                    </label>
                </Font.FuturaBook>
            </div>

            <div className={`${styles.input_container} ${styles.fullwidth}`}>
                <Checkbox
                    id="optinUtmbLive"
                    name="optinUtmbLive"
                    checked={formik.values.optinUtmbLive}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                />
                <Font.FuturaBook mobile="16" desktop="18">
                    <label htmlFor="optinUtmbLive">
                        <span dangerouslySetInnerHTML={{ __html: t('myutmb:myUtmb.registration.optinUtmbLive') }} />
                    </label>
                </Font.FuturaBook>
            </div>

            <ActionsButtons onCancel={onCancel} />
        </form>
    );
};
